import PropTypes from 'prop-types';
import find from 'lodash/find';
import pick from 'lodash/pick';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { useDDPCall } from '@zedoc/ddp-connector';
import * as presetSdk from '@preset-sdk/embedded';
// eslint-disable-next-line import/no-extraneous-dependencies
import { embedDashboard } from '@superset-ui/embedded-sdk';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { default as ProjectDashboardSelect } from '../../../../common/selectors/ProjectDashboard';
import {
  apiZedocGetPresetGuestToken,
  apiZedocGetSupersetGuestToken,
} from '../../../../common/api/zedoc';
import { notifyError } from '../../../../utils/notify';
import settings from '../../../../common/settings';
import ManageEmailReports from './components/ManageEmailReports';
import { useRum } from '../../../../containers/RumContext';

const {
  features: { enableEmailReports },
  presetEmbedding = {},
} = settings.public;
const { supersetDomain, supersetFlag } = presetEmbedding;

const getSupersetConnectError = (embeddedDashboardId) =>
  `Unable to connect with SuperSet (dashboard ID: ${embeddedDashboardId}). Please check connection string, user and password in env setup. Also ensure the target SuperSet is properly running and the user has proper access.`;

const SupersetConnectError = ({ embeddedDashboardId }) => (
  <div className="flex items-start justify-center gap-2 py-16 px-4 text-center">
    <ExclamationCircleIcon className="w-5 h-5 text-danger-500 flex-shrink-0 mt-0.5" />
    <p className="text-sm text-danger-500 max-w-lg m-0 text-left">
      {getSupersetConnectError(embeddedDashboardId)}
    </p>
  </div>
);

const Container = styled.div`
  ${({ $hidden }) => $hidden && 'display: none;'}

  iframe {
    overflow: hidden;
    background-color: transparent;
    width: 100%;
    height: 1000px;
    border: 0;
  }
`;

const DashboardPreset = ({
  projectId,
  recipientId,
  participationId,
  dashboardId,
}) => {
  const { i18n } = useTranslation();
  const ref = useRef();
  const [hasConnectError, setHasConnectError] = useState(false);
  const {
    ddpCall,
    // ddpIsPending,
  } = useDDPCall();
  const awsRum = useRum();

  const projectDashboard = useSelector(
    ProjectDashboardSelect.one().whereIdEquals(dashboardId),
  );

  const dashboardSettings = useMemo(() => {
    if (!projectDashboard || !projectDashboard.settings) {
      return {};
    }
    const finalSettings = pick(projectDashboard.settings, [
      'dashboardId',
      'uiConfig',
      'useOverlays',
      'disableOwnershipFilter',
    ]);
    const { translations } = projectDashboard.settings;
    const matchingTranslation = find(translations, { language: i18n.language });
    if (matchingTranslation) {
      finalSettings.dashboardId = matchingTranslation.dashboardId;
    }
    return finalSettings;
  }, [projectDashboard, i18n.language]);
  const embeddedDashboardId = dashboardSettings.dashboardId;
  useEffect(() => {
    let isCurrent = true;
    setHasConnectError(false);
    if (!dashboardSettings.dashboardId || !supersetDomain) {
      return () => {
        isCurrent = false;
      };
    }
    const params = {
      projectId,
      useOverlays: !!dashboardSettings.useOverlays,
      embeddedDashboardId: dashboardSettings.dashboardId,
      disableOwnershipFilter: !!dashboardSettings.disableOwnershipFilter,
    };
    if (recipientId) {
      params.recipientId = recipientId;
    }
    if (participationId) {
      params.participationId = participationId;
    }
    const tokenApi = supersetFlag
      ? apiZedocGetSupersetGuestToken
      : apiZedocGetPresetGuestToken;
    // Only ever surfaces the error banner for the effect run that is
    // still current, so a stale in-flight request for a previous
    // recipient/participation can't clobber a newer, valid dashboard.
    // Rethrows (rather than hanging forever) so the SDK's own guest-token
    // refresh cycle still gets a rejection it can react to, instead of
    // silently dying and leaking this closure.
    const onGuestTokenError = (err) => {
      if (isCurrent) {
        setHasConnectError(true);
      }
      notifyError()(err);
      throw err;
    };
    const startTime = new Date().getTime();
    const fetchGuestToken = () =>
      ddpCall(tokenApi.withParams(params))
        .then(({ token }) => {
          if (!token) {
            throw new Error('Guest token was not returned');
          }
          return token;
        })
        .catch(onGuestTokenError);
    const promise = supersetFlag
      ? embedDashboard({
          id: dashboardSettings.dashboardId,
          supersetDomain,
          mountPoint: document.getElementById('superset-container'),
          fetchGuestToken,
          dashboardUiConfig: {
            hideTitle: true,
            hideTab: false,
            hideChartControls: false,
            ...dashboardSettings.uiConfig,
            filters: {
              expanded: false,
              ...dashboardSettings.uiConfig?.filters,
            },
          },
        })
      : presetSdk.embedDashboard({
          id: dashboardSettings.dashboardId,
          supersetDomain,
          mountPoint: ref.current,
          fetchGuestToken,
          dashboardUiConfig: {
            hideTitle: true,
            hideTab: false,
            hideChartControls: false,
            ...dashboardSettings.uiConfig,
            filters: {
              expanded: false,
              ...dashboardSettings.uiConfig?.filters,
            },
          },
        });

    promise
      .then(() => {
        awsRum?.recordEvent('com.zedoc.rum.dashboard_preset_load', {
          duration: new Date().getTime() - startTime,
        });
      })
      .catch(() => {});
    return () => {
      isCurrent = false;
      promise
        .then((dashboard) => {
          if (dashboard) {
            dashboard.unmount();
          }
        })
        .catch(() => {});
    };
  }, [
    projectId,
    recipientId,
    participationId,
    ddpCall,
    dashboardSettings,
    awsRum,
  ]);

  // The mount point stays in the DOM even while hasConnectError is true
  // (hidden via CSS, not unmounted) so a later retry always finds a real
  // element instead of a null mountPoint.
  const dashboardContent = supersetFlag ? (
    <Container id="superset-container" $hidden={hasConnectError} />
  ) : (
    <Container ref={ref} $hidden={hasConnectError} />
  );
  const errorContent = hasConnectError && (
    <SupersetConnectError embeddedDashboardId={embeddedDashboardId} />
  );

  return supersetFlag ? (
    <div className="stack-2">
      {enableEmailReports && (
        <div className="self-end">
          <ManageEmailReports embeddedDashboardId={embeddedDashboardId} />
        </div>
      )}
      {errorContent}
      {dashboardContent}
    </div>
  ) : (
    <>
      {errorContent}
      {dashboardContent}
    </>
  );
};

SupersetConnectError.propTypes = {
  embeddedDashboardId: PropTypes.string.isRequired,
};

DashboardPreset.propTypes = {
  projectId: PropTypes.string.isRequired,
  recipientId: PropTypes.string,
  participationId: PropTypes.string,
  dashboardId: PropTypes.string.isRequired,
};

DashboardPreset.defaultProps = {
  recipientId: null,
  participationId: null,
};

export default DashboardPreset;
